'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthProvider';
import { useLogo } from './AppSettingsProvider';
import { useAnnouncements } from './AnnouncementsProvider';
import LeaveNotifications from './LeaveNotifications';
import type { PermissionKey } from '@/lib/permissions';

interface NavLink { href: string; label: string; permission: PermissionKey }
type NavItem = ({ kind: 'link' } & NavLink) | { kind: 'group'; label: string; children: NavLink[] };

const items: NavItem[] = [
  { kind: 'link', href: '/', label: '📊 Dashboard', permission: 'view.dashboard' },
  {
    kind: 'group',
    label: '📋 Tareas',
    children: [
      { href: '/tickets', label: '🎫 Tickets', permission: 'view.tickets' },
      { href: '/projects', label: '📁 Proyectos', permission: 'view.projects' },
      { href: '/assignments', label: '👥 Asignaciones', permission: 'view.assignments' },
      { href: '/tasks/admin', label: '🗂️ Tareas Administrativas', permission: 'view.my.admin.tasks' },
    ],
  },
  {
    kind: 'group',
    label: '☀️ Daily',
    children: [
      { href: '/daily', label: '☀️ Daily', permission: 'view.daily' },
      { href: '/daily-report', label: '📝 Reporte Daily', permission: 'view.daily.report' },
      { href: '/reports/stoppers', label: '🚧 Reporte de Stoppers', permission: 'view.reports.stoppers' },
    ],
  },
  {
    kind: 'group',
    label: '📊 Reportes',
    children: [
      { href: '/reports/admin-tasks', label: '🗂️ Tareas administrativas', permission: 'view.reports.admin.tasks' },
      { href: '/reports/closed-tickets', label: '🎯 Tickets cerrados', permission: 'view.reports.closed.tickets' },
      { href: '/reports/project-activities', label: '📋 Actividades de proyecto', permission: 'view.reports.project.activities' },
      { href: '/reports/stoppers-removed', label: '🗑️ Stoppers removidos', permission: 'view.reports.stoppers.removed' },
      { href: '/reports/stopper-impact', label: '⏱️ Impacto de stoppers', permission: 'view.reports.stopper.impact' },
      { href: '/reports/time-discrepancies', label: '⚖️ Diferencias de horas', permission: 'view.reports.time.discrepancies' },
      { href: '/reports/performance', label: '🧭 Reporte individual', permission: 'view.daily' },
      { href: '/daily-history', label: '📚 Histórico Daily', permission: 'view.daily.history' },
      { href: '/reports/absences', label: '🌴 Reporte de Ausencias', permission: 'view.leave.report' },
      { href: '/reports/leave-requests', label: '🔍 Auditoría de Ausencias', permission: 'view.leave.report' },
    ],
  },
  {
    kind: 'group',
    label: '🧑‍💼 RH',
    children: [
      { href: '/admin/leave-requests', label: '🏖️ Ausencias', permission: 'manage.leave' },
      { href: '/admin/reassignments', label: '🔁 Reasignar actividades', permission: 'manage.reassignments' },
    ],
  },
  {
    kind: 'group',
    label: '📁 PM',
    children: [
      { href: '/pm/upload', label: '📤 Cargar backlog', permission: 'view.pm.upload' },
    ],
  },
  {
    kind: 'group',
    label: '⚙️ Administración',
    children: [
      { href: '/admin/users', label: '👤 Usuarios', permission: 'view.admin.users' },
      { href: '/admin/roles', label: '🛡️ Roles', permission: 'view.admin.roles' },
      { href: '/admin/teams', label: '👥 Equipos', permission: 'view.admin.teams' },
      { href: '/admin/tasks', label: '🗂️ Tareas administrativas', permission: 'view.admin.tasks' },
      { href: '/admin/api-tokens', label: '🔑 Tokens de API', permission: 'manage.api.tokens' },
      { href: '/admin/reports/weekly', label: '📤 Reporte semanal por cliente', permission: 'manage.reports.weekly' },
      { href: '/admin/announcements', label: '📢 Avisos', permission: 'view.admin.announcements' },
      { href: '/admin/settings', label: '🛠️ Configuración', permission: 'manage.app.settings' },
    ],
  },
];

export default function Navbar() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [compact, setCompact] = useState(false);
  const logoNavbar = useLogo('navbar');
  const { user, hasPermission, logout, loading } = useAuth();

  const navRef = useRef<HTMLElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOpen(false);
    setUserMenuOpen(false);
    setOpenGroup(null);
  }, [pathname]);

  // Medir si los links caben en horizontal; si no, activar modo compacto
  useEffect(() => {
    if (!navRef.current || !measureRef.current) return;

    const check = () => {
      if (!navRef.current || !measureRef.current) return;
      const available = navRef.current.clientWidth;
      const needed = measureRef.current.scrollWidth;
      // 24px de buffer para evitar oscilación
      setCompact(needed > available - 24);
    };

    const ro = new ResizeObserver(check);
    ro.observe(navRef.current);
    check();
    return () => ro.disconnect();
  }, [user, loading]);

  if (pathname === '/login') return null;

  const visibleItems: NavItem[] = items
    .map<NavItem | null>(it => {
      if (it.kind === 'link') {
        return hasPermission(it.permission) ? it : null;
      }
      const visibleChildren = it.children.filter(c => hasPermission(c.permission));
      if (visibleChildren.length === 0) return null;
      return { kind: 'group', label: it.label, children: visibleChildren };
    })
    .filter((x): x is NavItem => x !== null);

  const userLabel = user ? (user.name || user.email.split('@')[0]) : '';

  return (
    <nav ref={navRef} className={`navbar ${compact ? 'is-compact' : ''}`}>
      <div className="navbar-brand">
        {logoNavbar ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={logoNavbar} alt="Logo" style={{ height: '32px', maxWidth: '200px', objectFit: 'contain' }} />
        ) : (
          <>
            <span style={{ fontSize: '1.5rem' }}>⚡</span>
            <span className="neon-text-cyan" style={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.05em' }}>
              Team Dashboard
            </span>
          </>
        )}
      </div>

      <button
        className="navbar-toggle"
        onClick={() => setOpen(!open)}
        aria-label="Toggle menu"
        aria-expanded={open}
      >
        <span className={`hamburger ${open ? 'is-open' : ''}`}>
          <span></span><span></span><span></span>
        </span>
      </button>

      <div className={`navbar-links ${open ? 'is-open' : ''}`}>
        {visibleItems.map(item => {
          if (item.kind === 'link') {
            return (
              <Link
                key={item.href}
                href={item.href}
                className={`nav-link ${pathname === item.href ? 'active' : ''}`}
              >
                {item.label}
              </Link>
            );
          }
          const isGroupOpen = openGroup === item.label;
          const isGroupActive = item.children.some(c => pathname === c.href);
          return (
            <div key={item.label} style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setOpenGroup(isGroupOpen ? null : item.label)}
                className={`nav-link ${isGroupActive ? 'active' : ''}`}
                style={{
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                  lineHeight: 'inherit',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                }}
                aria-expanded={isGroupOpen}
              >
                {item.label}
                <span style={{ fontSize: '0.6rem', opacity: 0.8 }}>▼</span>
              </button>
              {isGroupOpen && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 6px)', left: 0,
                  minWidth: '180px', background: 'var(--bg-card)',
                  border: '1px solid var(--border-color)', borderRadius: '8px',
                  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.5)', zIndex: 50, padding: '0.4rem',
                  display: 'flex', flexDirection: 'column', gap: '0.2rem',
                }}>
                  {item.children.map(child => (
                    <Link
                      key={child.href}
                      href={child.href}
                      className={`nav-link ${pathname === child.href ? 'active' : ''}`}
                      style={{ display: 'block' }}
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!loading && user && <AnnouncementRestore />}

      {!loading && user && <LeaveNotifications />}

      {!loading && user && (
        <div className="navbar-user" style={{ position: 'relative' }}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            style={{
              background: 'rgba(0, 245, 255, 0.08)',
              border: '1px solid rgba(0, 245, 255, 0.3)',
              borderRadius: '8px',
              color: 'var(--neon-cyan)',
              padding: '0.4rem 0.8rem',
              fontSize: '0.8rem',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              maxWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={user.email}
          >
            <span>👤</span>
            <span className="navbar-user-label">{userLabel}</span>
            <span style={{ fontSize: '0.65rem' }}>▼</span>
          </button>
          {userMenuOpen && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 6px)', right: 0,
              minWidth: '220px', background: 'var(--bg-card)',
              border: '1px solid var(--border-color)', borderRadius: '8px',
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.5)', zIndex: 50, padding: '0.5rem',
            }}>
              <div style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border-color)', marginBottom: '0.4rem' }}>
                <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{user.name || '—'}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{user.email}</div>
                {user.role_name && (
                  <div style={{ fontSize: '0.7rem', color: 'var(--neon-purple)', marginTop: '0.2rem' }}>
                    🛡️ {user.role_name}
                  </div>
                )}
              </div>
              {hasPermission('view.leave.own') && (
                <Link
                  href="/leave-requests"
                  className={pathname === '/leave-requests' ? 'active' : ''}
                  style={{
                    display: 'block', textAlign: 'left', color: 'var(--neon-cyan)',
                    padding: '0.5rem 0.75rem', borderRadius: '6px',
                    fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none',
                  }}
                >
                  🏖️ Solicitudes de Ausencia
                </Link>
              )}
              <Link
                href="/change-password"
                style={{
                  display: 'block', textAlign: 'left', color: 'var(--neon-cyan)',
                  padding: '0.5rem 0.75rem', borderRadius: '6px',
                  fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none',
                }}
              >
                🔑 Cambiar contraseña
              </Link>
              <button
                onClick={logout}
                style={{
                  width: '100%', textAlign: 'left', background: 'transparent',
                  border: 'none', color: 'var(--neon-red)', padding: '0.5rem 0.75rem',
                  borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                }}
              >
                ↗ Cerrar sesión
              </button>
            </div>
          )}
        </div>
      )}

      {/* Container oculto para medir el ancho real necesario en modo horizontal */}
      <div ref={measureRef} className="navbar-measure" aria-hidden="true">
        <span className="navbar-measure-brand">⚡ Team Dashboard</span>
        {visibleItems.map(it => (
          <span key={it.kind === 'link' ? it.href : it.label} className="navbar-measure-link">
            {it.kind === 'link' ? it.label : `${it.label} ▼`}
          </span>
        ))}
        {user && <span className="navbar-measure-user">👤 {userLabel} ▼</span>}
      </div>
    </nav>
  );
}

function AnnouncementRestore() {
  const { keptCount, restoreKept } = useAnnouncements();
  if (keptCount === 0) return null;
  const label = keptCount === 1 ? 'Mostrar el aviso oculto' : `Mostrar ${keptCount} avisos ocultos`;
  return (
    <button
      onClick={restoreKept}
      title={label}
      aria-label={label}
      style={{
        position: 'relative',
        background: 'rgba(245, 255, 0, 0.08)',
        border: '1px solid rgba(245, 255, 0, 0.4)',
        borderRadius: '8px',
        color: 'var(--neon-yellow)',
        padding: '0.4rem 0.6rem',
        fontSize: '1rem',
        lineHeight: 1,
        cursor: 'pointer',
        boxShadow: '0 0 6px rgba(245, 255, 0, 0.25)',
      }}
    >
      📣
      {keptCount > 1 && (
        <span style={{
          position: 'absolute', top: '-6px', right: '-6px',
          background: 'var(--neon-yellow)', color: 'var(--bg-primary)',
          borderRadius: '999px', fontSize: '0.62rem', fontWeight: 800,
          minWidth: '16px', height: '16px', display: 'flex', alignItems: 'center',
          justifyContent: 'center', padding: '0 3px',
        }}>
          {keptCount}
        </span>
      )}
    </button>
  );
}
